'use client';
import { useState } from 'react';
import LinkWrapper from '../link-wrapper/link-wrapper';
import { PageJournalFragmentFragment } from '@fm-nx/contentful-types';
import AnimationImage from '../animation-image/animation-image';
import { CldImage } from 'next-cloudinary';
import { formatDateLong } from '../helpers/date-utils';

export interface FeaturedCardJournalProps {
    page?: PageJournalFragmentFragment | null;
}

export function FeaturedCardJournal(props: FeaturedCardJournalProps) {
    const { page } = props;
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    if (!page) return null;

    const imageCloudinary = page.heroImage?.cloudinaryMedia[0];
    const authors = page.authorsCollection ? page.authorsCollection.items : null;

    const formattedDate = formatDateLong(page.publishDate);

    return (
        <div className="">
            {page.heroImage?.cloudinaryMedia && (
                <div className="py-10 mx-6 lg:mx-0">
                    <LinkWrapper page={page} className="group">
                        <div className="items-center grid-cols-1 gap-0 mx-6 group md:mx-0 md:grid md:grid-cols-14 md:gap-8">
                            <div className="col-span-4 col-start-2 ">
                                <div className="mb-2 space-y-2 md:mb-8">
                                    <div className="text-gray-900 h5 overline">Article</div>
                                    {authors && (
                                        <div className="text-sm text-gray-900">
                                            by{' '}
                                            {authors.map((author, index) => {
                                                if (!author || !author.person) return null;
                                                return (
                                                    <div key={author.sys.id} className="inline">
                                                        {`${author.person.firstName} ${author.person.lastName}`}
                                                        {index < authors.length - 1 && ' + '}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {formattedDate && <div className="text-sm text-gray-900">{formattedDate}</div>}
                                    <div className={`text-gray-900 h-lg group-hover:text-green-400`}>{page.title}</div>
                                    <p className={`text-gray-900`}>{page.summary}</p>
                                </div>

                                <div className="inline-block mt-4 text-gray-900 h5 emdash-push">Read More</div>
                            </div>

                            {imageCloudinary && (
                                <div className="col-span-9 mt-6 md:mt-0">
                                    <AnimationImage isImageLoaded={isImageLoaded}>
                                        <div className="relative w-full min-h-[370px] md:min-h-[550px]">
                                            <CldImage
                                                fill
                                                className="object-cover w-full min-h-[370px] md:min-h-[550px]"
                                                src={`${imageCloudinary.public_id}`}
                                                onLoad={() => setIsImageLoaded(true)}
                                                alt={page.title || 'Featured journal article'}
                                                format="auto" // Automatically use WebP or AVIF based on browser support
                                                quality="auto" // Automatically adjust quality based on network and device
                                                dpr="auto" // Automatically adjust for device pixel ratio
                                                sizes="(max-width: 768px) 100vw, 66vw"
                                                placeholder="blur"
                                                blurDataURL="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YzZjRmNiIvPjwvc3ZnPg=="
                                            />
                                        </div>
                                    </AnimationImage>
                                </div>
                            )}
                        </div>
                    </LinkWrapper>
                </div>
            )}
        </div>
    );
}

export default FeaturedCardJournal;
